import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import { ShieldCheck } from "lucide-react";
import "../ModalEmail/style.css";
import AccessModal from "../Access_modal";
import { decodeId } from "../../../../utils/idObfuscator";

const accessAreas = [
  { id: "business-dashboard", label: "Business dashboard" },
  { id: "messanger", label: "Messenger" },
  { id: "shop-reports", label: "Shop reports and suggestions" },
  { id: "post-edit-items", label: "Post and edit items" },
];

const SpecificModal = ({
  handleCloseaccesstwo,
  showaccesstwo,
  email,
  onGrantAccess,
  onClose,
  onSaveAccess,
  shopId,
  initialAreas = [],
}) => {
  const resolvedShopId = decodeId(shopId) || shopId || "";
  const [showaccess, setShowaccess] = useState(false);
  const [openAccessAfterExit, setOpenAccessAfterExit] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [membershipId, setMembershipId] = useState("");
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState("");
  const transitioningRef = React.useRef(false);

  useEffect(() => {
    if (showaccesstwo) {
      setSelectedAreas(Array.isArray(initialAreas) ? [...initialAreas] : []);
    }
  }, [showaccesstwo, initialAreas]);

  const handleCloseaccess = () => {
    transitioningRef.current = false;
    setShowaccess(false);
    setSelectedAreas([]);
    setMembershipId("");
    onClose();
  };

  const handleShowaccess = async () => {
    setAccessError("");
    if (selectedAreas.length === 0) {
      setAccessError("Select at least one access area to continue.");
      return;
    }

    setIsSavingAccess(true);
    try {
      const savedResult = await onSaveAccess(selectedAreas);
      const isAlreadyActive =
        typeof savedResult === "object" && savedResult !== null
          ? Boolean(savedResult.alreadyActive)
          : false;

      if (isAlreadyActive) {
        toast.success(
          savedResult?.message || "You have access based on shop access",
          { position: "top-center" },
        );
        transitioningRef.current = false;
        setOpenAccessAfterExit(false);
        handleCloseaccesstwo();
        onClose();
        return;
      }

      const savedMembershipId =
        typeof savedResult === "object" && savedResult !== null
          ? savedResult.membershipId
          : savedResult;
      setMembershipId(savedMembershipId);
      transitioningRef.current = true;
      setOpenAccessAfterExit(true);
      handleCloseaccesstwo();
      window.setTimeout(() => {
        if (transitioningRef.current) {
          transitioningRef.current = false;
          setOpenAccessAfterExit(false);
          setShowaccess(true);
        }
      }, 300);
    } catch (error) {
      setAccessError(
        error?.data?.message || "Unable to save access areas. Please try again.",
      );
    } finally {
      setIsSavingAccess(false);
    }
  };

  const handleModalExited = () => {
    if (transitioningRef.current || openAccessAfterExit) {
      transitioningRef.current = false;
      setOpenAccessAfterExit(false);
      setShowaccess(true);
    }
  };

  const handleGrantAccess = (username) => {
    transitioningRef.current = false;
    setShowaccess(false);
    onGrantAccess(username);
  };

  const handleDismiss = () => {
    transitioningRef.current = false;
    setOpenAccessAfterExit(false);
    setSelectedAreas([]);
    setMembershipId("");
    handleCloseaccesstwo();
    onClose();
  };

  return (
    <>
      <AccessModal
        handleCloseaccess={handleCloseaccess}
        showaccess={showaccess}
        email={email}
        onGrantAccess={handleGrantAccess}
        shopId={resolvedShopId}
        membershipId={membershipId}
      />
      <Modal
        className="shop-invite-modal shop-member-access-modal"
        centered
        show={showaccesstwo}
        onHide={handleDismiss}
        onExited={handleModalExited}
        aria-labelledby="shop-member-access-title"
      >
        <Modal.Header closeButton className="shop-invite-header">
          <div className="shop-invite-heading">
            <span className="shop-invite-heading-icon">
              <ShieldCheck size={20} />
            </span>
            <div>
              <span className="shop-invite-step-badge">Step 1 of 2</span>
              <Modal.Title id="shop-member-access-title">
                Choose member access
              </Modal.Title>
              <p>Select the areas this member can access.</p>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="shop-invite-body">
          {email && <p className="shop-member-access-email">{email}</p>}
          <Form className="shop-member-access-options">
            {accessAreas.map(({ id, label }) => (
              <Form.Check
                key={id}
                type="checkbox"
                id={`shop-access-${id}`}
                className={`shop-member-access-option ${
                  selectedAreas.includes(id) ? "is-selected" : ""
                }`}
                label={label}
                checked={selectedAreas.includes(id)}
                onChange={(event) => {
                  setSelectedAreas((current) =>
                    event.target.checked
                      ? [...current, id]
                      : current.filter((area) => area !== id),
                  );
                  setAccessError("");
                }}
              />
            ))}
          </Form>
          <p className="shop-member-access-count" aria-live="polite">
            {selectedAreas.length
              ? `${selectedAreas.length} ${
                  selectedAreas.length === 1 ? "area" : "areas"
                } selected`
              : "Choose at least one area to continue."}
          </p>
          {accessError && (
            <div className="shop-invite-invalid" role="alert">
              {accessError}
            </div>
          )}
          <div className="shop-invite-actions">
            <button
              type="button"
              className="shop-invite-cancel"
              onClick={handleDismiss}
            >
              Cancel
            </button>
            <button
              type="button"
              className="shop-invite-next"
              onClick={handleShowaccess}
              disabled={isSavingAccess}
            >
              {isSavingAccess ? "Saving..." : "Next"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SpecificModal;
